import { ApiClient } from '@/services/api/client'
import { ItemListCondition, serializeForApi } from '@hl-itemlist/model/condition'

/**
 * ホテラバ専用商品一覧はhl2(SINGLEインスタンス)のホスト直下でのみ動作するため、パスは固定。
 */
export const API_BASE_PATH = '/api/hl-proxy/colored-contact-lens/itemlist'
export const PRICES_API_PATH = '/api/hl-proxy/fku-items/prices'

export interface PaginationMeta {
  readonly currentPage: number
  readonly itemCount: number
  readonly itemsPerPage: number
  readonly totalItems: number
  readonly totalPages: number
}

export interface LensSize {
  readonly value: string
  readonly label: string
}

export interface ProductRoot {
  readonly gpRootId: string
  readonly productName: string
}

/**
 * 一覧APIの商品(FKU)要素。
 *
 * レスポンスのうち**表示に使うフィールドだけ**を記載する(利用しないフィールドは受信時点で無視し、
 * 型にも検証にも足さない。何が取得できるかの正はswagger/BFF側DTOであり、ここに写しを持たない)。
 *
 * 一覧はspec完全な商品(isVisible=true)だけを返す契約のため、サイズ3種と画像URLは非null
 * (任意登録のlayerImageUrlのみnull許容)。契約の実体はBFF側のDTOで、spec未確定の商品が
 * 混ざる経路はBFF側のassertが弾く。
 * @see https://github.com/Everforth/pj-hotel-lovers-api/blob/develop/src/products/dto/products.mobile.response.dto.ts FkuItemColoredContactLensMobileDto
 */
export interface ColoredContactLensFkuItem {
  readonly id: number
  readonly colorCode: string
  readonly colorName: string
  readonly thumbnailUrl: string
  readonly layerImageUrl: string | null
  readonly lensImageUrl: string
  readonly modelImageUrl: string
  readonly diameter: LensSize
  readonly graphicDiameter: LensSize
  readonly baseCurve: LensSize
  readonly expirationPeriod: string
  readonly isSuikou: boolean
  readonly isNonRotatingSuikou: boolean
  readonly isSilicon: boolean
  readonly root: ProductRoot
}

export interface ItemListResponse {
  readonly meta: PaginationMeta
  readonly items: ColoredContactLensFkuItem[]
}

export interface LensSizeCandidates {
  readonly diameters: LensSize[]
  readonly graphicDiameters: LensSize[]
  readonly baseCurves: LensSize[]
}

export type DiscountMethod = 'percentage' | 'fixed'

export interface FkuItemPriceDiscount {
  readonly method: DiscountMethod
  readonly value: number
}

/**
 * 商品(FKU)の表示価格。一覧のレスポンスに価格は含まれないため別APIで取得する。
 * displayPriceIncludingTax はセール中はセール後の価格が入る(値引き内容は discount が補足する)。
 */
export interface FkuItemPrice {
  readonly fkuItemId: number
  readonly underSale: boolean
  readonly displayPriceIncludingTax: number
  readonly hasRange: boolean
  readonly discount: FkuItemPriceDiscount | null
}

/**
 * ホテラバアプリBFFのカラコン商品一覧APIを、frontの純proxy (/api/hl-proxy) 経由で呼ぶ。
 * proxyはレスポンスを整形しないため、ボディの形はBFFのレスポンスそのもの(封筒なしの `{meta, items}`)。
 * レスポンスは信頼できない外部入力として、この境界で要素単位まで実行時検証してから型の付いた値として返す。
 * 契約を満たさない要素は間引かずエラーにする(上流の契約違反をフォールバック表示として隠さない)。
 */
export class HlItemListApi {
  /**
   * 指定した絞り込み条件・ページの商品一覧を取得する。
   */
  async fetchList(condition: ItemListCondition, page: number): Promise<ItemListResponse> {
    const response = await ApiClient.get(API_BASE_PATH, serializeForApi(condition, page))

    return validateItemListResponse(response.body)
  }

  /**
   * 指定した絞り込み条件に該当する商品の総数を取得する(絞り込みモーダルの件数プレビュー用)。
   */
  async fetchStats(condition: ItemListCondition): Promise<number> {
    const params = serializeForApi(condition)
    // statsは並び順を解釈しない。送らないことで、cache-as-publicのキャッシュキーが並び順の数だけ分かれるのを防ぐ
    delete params.sort

    const response = await ApiClient.get(`${API_BASE_PATH}/stats`, params)

    return validateStatsResponse(response.body)
  }

  /**
   * レンズサイズ(DIA・着色直径・BC)の絞り込み候補を取得する。
   */
  async fetchLensSizeCandidates(): Promise<LensSizeCandidates> {
    const response = await ApiClient.get(`${API_BASE_PATH}/lens-size-candidates`)

    return validateLensSizeCandidatesResponse(response.body)
  }

  /**
   * 指定した商品(FKU)の表示価格をまとめて取得する(一覧のページ単位でのバッチ呼び出しを想定)。
   */
  async fetchPrices(fkuItemIds: number[]): Promise<FkuItemPrice[]> {
    if (fkuItemIds.length === 0) {
      return []
    }

    const response = await ApiClient.get(PRICES_API_PATH, { 'fkuItemIds[]': fkuItemIds })

    return validatePricesResponse(response.body)
  }
}

function validateItemListResponse(body: unknown): ItemListResponse {
  const candidate = body as { meta?: unknown, items?: unknown } | undefined
  const meta = candidate ? candidate.meta : undefined
  const items = candidate ? candidate.items : undefined

  if (!isPaginationMeta(meta) || !Array.isArray(items)) {
    throw new Error('Unexpected itemlist response: meta or items is missing')
  }

  return {
    meta,
    items: items.map(toValidatedFkuItem),
  }
}

function toValidatedFkuItem(item: unknown, index: number): ColoredContactLensFkuItem {
  if (!isColoredContactLensFkuItem(item)) {
    throw new Error(`Unexpected itemlist response: items[${index}] does not satisfy the contract`)
  }

  return item
}

function validateStatsResponse(body: unknown): number {
  const candidate = body as { total?: unknown } | undefined

  if (!candidate || typeof candidate.total !== 'number') {
    throw new Error('Unexpected stats response: total is missing')
  }

  return candidate.total
}

function validateLensSizeCandidatesResponse(body: unknown): LensSizeCandidates {
  const candidate =
    body as { diameters?: unknown, graphicDiameters?: unknown, baseCurves?: unknown } | undefined

  if (
    !candidate ||
    !Array.isArray(candidate.diameters) ||
    !Array.isArray(candidate.graphicDiameters) ||
    !Array.isArray(candidate.baseCurves)
  ) {
    throw new Error('Unexpected lens size candidates response: candidate arrays are missing')
  }

  return {
    diameters: candidate.diameters.map((item, index) => toValidatedLensSize('diameters', item, index)),
    graphicDiameters:
      candidate.graphicDiameters.map((item, index) => toValidatedLensSize('graphicDiameters', item, index)),
    baseCurves: candidate.baseCurves.map((item, index) => toValidatedLensSize('baseCurves', item, index)),
  }
}

function toValidatedLensSize(field: string, item: unknown, index: number): LensSize {
  if (!isLensSize(item)) {
    throw new Error(`Unexpected lens size candidates response: ${field}[${index}] does not satisfy the contract`)
  }

  return item
}

function validatePricesResponse(body: unknown): FkuItemPrice[] {
  const candidate = body as { items?: unknown } | undefined
  const items = candidate ? candidate.items : undefined

  if (!Array.isArray(items)) {
    throw new Error('Unexpected prices response: items is missing')
  }

  return items.map(toValidatedPrice)
}

function toValidatedPrice(item: unknown, index: number): FkuItemPrice {
  if (!isFkuItemPrice(item)) {
    throw new Error(`Unexpected prices response: items[${index}] does not satisfy the contract`)
  }

  return item
}

function isRecord(value: unknown): value is { [key: string]: unknown } {
  return typeof value === 'object' && value !== null
}

function isPaginationMeta(value: unknown): value is PaginationMeta {
  if (!isRecord(value)) {
    return false
  }

  return typeof value.currentPage === 'number' &&
    typeof value.itemCount === 'number' &&
    typeof value.itemsPerPage === 'number' &&
    typeof value.totalItems === 'number' &&
    typeof value.totalPages === 'number'
}

function isLensSize(value: unknown): value is LensSize {
  if (!isRecord(value)) {
    return false
  }

  return typeof value.value === 'string' && typeof value.label === 'string'
}

function isProductRoot(value: unknown): value is ProductRoot {
  if (!isRecord(value)) {
    return false
  }

  return typeof value.gpRootId === 'string' &&
    typeof value.productName === 'string'
}

function isColoredContactLensFkuItem(value: unknown): value is ColoredContactLensFkuItem {
  if (!isRecord(value)) {
    return false
  }

  return typeof value.id === 'number' &&
    typeof value.colorCode === 'string' &&
    typeof value.colorName === 'string' &&
    typeof value.thumbnailUrl === 'string' &&
    (value.layerImageUrl === null || typeof value.layerImageUrl === 'string') &&
    typeof value.lensImageUrl === 'string' &&
    typeof value.modelImageUrl === 'string' &&
    isLensSize(value.diameter) &&
    isLensSize(value.graphicDiameter) &&
    isLensSize(value.baseCurve) &&
    typeof value.expirationPeriod === 'string' &&
    typeof value.isSuikou === 'boolean' &&
    typeof value.isNonRotatingSuikou === 'boolean' &&
    typeof value.isSilicon === 'boolean' &&
    isProductRoot(value.root)
}

function isFkuItemPriceDiscount(value: unknown): value is FkuItemPriceDiscount {
  if (!isRecord(value)) {
    return false
  }

  return (value.method === 'percentage' || value.method === 'fixed') &&
    typeof value.value === 'number'
}

function isFkuItemPrice(value: unknown): value is FkuItemPrice {
  if (!isRecord(value)) {
    return false
  }

  return typeof value.fkuItemId === 'number' &&
    typeof value.underSale === 'boolean' &&
    typeof value.displayPriceIncludingTax === 'number' &&
    typeof value.hasRange === 'boolean' &&
    (value.discount === null || isFkuItemPriceDiscount(value.discount))
}
